import { useState, useEffect, useCallback } from "react";
import { navLinks, salonInfo } from "@/mocks/content";

const MenuIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const CloseIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = useCallback((href: string) => {
    setMenuOpen(false);
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "bg-[#FAFAF8]/95 backdrop-blur-md border-b border-[#E8E6E1]" : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          {/* Logo */}
          <a
            href="#"
            onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}
            className="cursor-pointer"
          >
            <span
              className={`font-serif text-lg md:text-xl tracking-[0.2em] font-light transition-colors duration-300 ${
                scrolled ? "text-[#2C2C2C]" : "text-[#FAFAF8]"
              }`}
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              {salonInfo.name}
            </span>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.href}
                onClick={() => handleNav(link.href)}
                className={`relative text-sm tracking-[0.12em] font-light transition-colors duration-300 cursor-pointer whitespace-nowrap group ${
                  scrolled ? "text-[#2C2C2C]" : "text-[#FAFAF8]"
                }`}
                style={{ fontFamily: "'Noto Serif JP', serif" }}
              >
                {link.label}
                <span className="absolute -bottom-0.5 left-0 w-0 h-px bg-[#8B7355] transition-all duration-400 group-hover:w-full" />
              </button>
            ))}
          </nav>

          {/* Mobile Hamburger */}
          <button
            className={`md:hidden cursor-pointer transition-colors duration-300 ${
              scrolled ? "text-[#2C2C2C]" : "text-[#FAFAF8]"
            }`}
            onClick={() => setMenuOpen(true)}
            aria-label="メニューを開く"
          >
            <MenuIcon />
          </button>
        </div>
      </header>

      {/* Mobile Overlay */}
      <div
        className={`fixed inset-0 z-[100] transition-all duration-600 ${
          menuOpen ? "pointer-events-auto" : "pointer-events-none"
        }`}
        style={{
          background: "#2C2C2C",
          clipPath: menuOpen
            ? "polygon(0 0, 100% 0, 100% 100%, 0 100%)"
            : "polygon(100% 0, 100% 0, 100% 100%, 100% 100%)",
          transition: "clip-path 0.6s cubic-bezier(0.77, 0, 0.175, 1)",
        }}
      >
        <div className="flex flex-col h-full px-8 py-8">
          {/* Close */}
          <div className="flex justify-between items-center mb-16">
            <span
              className="text-[#FAFAF8]/60 text-sm tracking-[0.2em]"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              MENU
            </span>
            <button
              className="text-[#FAFAF8] cursor-pointer"
              onClick={() => setMenuOpen(false)}
              aria-label="メニューを閉じる"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Nav Items */}
          <nav className="flex flex-col gap-2 flex-1 justify-center">
            {navLinks.map((link, i) => (
              <button
                key={link.href}
                onClick={() => handleNav(link.href)}
                className="text-left cursor-pointer group"
                style={{
                  opacity: menuOpen ? 1 : 0,
                  transform: menuOpen ? "translateY(0)" : "translateY(30px)",
                  transition: `opacity 0.5s ease ${0.1 + i * 0.08}s, transform 0.5s ease ${0.1 + i * 0.08}s`,
                }}
              >
                <span
                  className="text-3xl font-light text-[#FAFAF8] tracking-[0.1em] group-hover:text-[#8B7355] transition-colors duration-300"
                  style={{ fontFamily: "'Noto Serif JP', serif" }}
                >
                  {link.label}
                </span>
                <div className="mt-1 w-0 h-px bg-[#8B7355] group-hover:w-16 transition-all duration-400" />
              </button>
            ))}
          </nav>

          {/* Bottom Info */}
          <div
            className="text-[#FAFAF8]/40 text-xs tracking-widest"
            style={{
              opacity: menuOpen ? 1 : 0,
              transition: "opacity 0.5s ease 0.5s",
              fontFamily: "'Noto Serif JP', serif",
            }}
          >
            <p>{salonInfo.phone}</p>
            <p className="mt-1">{salonInfo.address}</p>
          </div>
        </div>
      </div>
    </>
  );
}
